import { stAgo } from "../../i18n/streamingPagesPresentation";
import { stText, useStandaloneLanguage } from "../../i18n/streamingPagesPresentation";
/**
 * UserListPanel -- SideFlyout overlay for browsing all upstream media server users.
 *
 * Fetches the full user list from configured Emby/Jellyfin servers and presents
 * them in a searchable list. Clicking a user triggers the User Admin Panel.
 */
import { useMemo, useState } from "react";

import { Badge } from "../ui/Badge";
import { Icon } from "../ui/Icon";
import { Input } from "../ui/Input";
import { SideFlyout } from "../ui/SideFlyout";
import { Spinner } from "../ui/Spinner";
import { Tooltip } from "../ui/Tooltip";
import { cn } from "../../utils/cn";
import { useStreamingUsers, type UpstreamUser } from "../../hooks/useStreamingUsers";
import { getServerIcon, getServerColorClass } from "../../utils/serverDisplay";

// ============================================================================
// Types
// ============================================================================

export type UserListPanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when a user row is clicked -- opens UserAdminPanel for that user */
  onUserSelect: (userId: string, userName: string, serverType: string) => void;
};

// ============================================================================
// Helpers
// ============================================================================

/** Format a date string into relative time (e.g., "2 hours ago") */
function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return stText("never");
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return stText("unknown");

  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return stText("justNow");

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return stText("justNow");
  if (minutes < 60) return stAgo(minutes, "minute");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return stAgo(hours, "hour");

  const days = Math.floor(hours / 24);
  if (days < 30) return stAgo(days, "day");

  const months = Math.floor(days / 30);
  if (months < 12) return stAgo(months, "month");

  return stAgo(Math.floor(months / 12), "year");
}

// ============================================================================
// User Row
// ============================================================================

type UserRowProps = {
  user: UpstreamUser;
  onSelect: (userId: string, userName: string, serverType: string) => void;
};

const UserRow = ({ user, onSelect }: UserRowProps) => {
  useStandaloneLanguage();
  const [avatarError, setAvatarError] = useState(false);

  const isDisabled = user.Policy?.IsDisabled ?? false;
  const isAdmin = user.Policy?.IsAdministrator ?? false;
  const isHidden = user.Policy?.IsHidden ?? false;

  // Build avatar URL
  const avatarUrl =
    user.PrimaryImageTag && !avatarError
      ? `/api/streaming/users/${user.serverType}/${user.Id}/avatar`
      : null;

  return (
    <button
      type="button"
      className={cn(
        "flex items-center gap-3 w-full px-4 py-3 text-left",
        "hover:bg-muted/20 transition-colors border-b border-border/20 last:border-b-0",
        isDisabled && "opacity-60"
      )}
      onClick={() => onSelect(user.Id, user.Name, user.serverType)}
      aria-label={stText("openAdminPanelForValue", { value: user.Name })}
    >
      {/* Avatar */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="h-9 w-9 rounded-full object-cover bg-muted/30 flex-shrink-0"
          onError={() => setAvatarError(true)}
        />
      ) : (
        <div className="flex h-9 w-9 rounded-full bg-primary/10 text-primary-text font-semibold text-sm items-center justify-center flex-shrink-0">
          {user.Name.charAt(0).toUpperCase()}
        </div>
      )}

      {/* Name + Meta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">
            {user.Name}
          </span>
          <Icon
            icon={getServerIcon(user.serverType)}
            className={cn("h-3.5 w-3.5 flex-shrink-0", getServerColorClass(user.serverType))}
            aria-hidden
          />
          {isAdmin && <Badge variant="primary" size="xs">{stText("admin")}</Badge>}
          {isDisabled && <Badge variant="danger" size="xs">{stText("disabled")}</Badge>}
          {isHidden && <Badge variant="muted" size="xs">{stText("hidden")}</Badge>}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <Tooltip content={user.LastActivityDate ?? stText("noRecentActivity")}>
            <span className="text-[11px] text-muted-foreground cursor-help">{stText("inline10", { value0: formatRelativeTime(user.LastActivityDate) })}</span>
          </Tooltip>
        </div>
      </div>

      {/* Chevron */}
      <Icon icon="lucide:chevron-right" className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" aria-hidden />
    </button>
  );
};

// ============================================================================
// Component
// ============================================================================

export const UserListPanel = ({
  open,
  onOpenChange,
  onUserSelect,
}: UserListPanelProps) => {
  useStandaloneLanguage();
  const [search, setSearch] = useState("");

  // Fetch all server users when the panel is open
  const { users, isLoading, error, refetch } = useStreamingUsers(open);

  // Filter by search term
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const term = search.toLowerCase().trim();
    if (!term) return users;
    return users.filter((u) => u.Name.toLowerCase().includes(term));
  }, [users, search]);

  // Sort: admins first, then alphabetical
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aAdmin = a.Policy?.IsAdministrator ? 0 : 1;
      const bAdmin = b.Policy?.IsAdministrator ? 0 : 1;
      if (aAdmin !== bAdmin) return aAdmin - bAdmin;
      return a.Name.localeCompare(b.Name);
    });
  }, [filteredUsers]);

  return (
    <SideFlyout
      open={open}
      onOpenChange={onOpenChange}
      title={stText("serverUsers")}
      icon="lucide:server-cog"
    >
      <div className="flex flex-col h-full">
        {/* Search + Stats Bar */}
        <div className="px-4 py-3 border-b border-border/30 space-y-2 shrink-0">
          <Input
            placeholder={stText("searchUsers")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              {users ? stText("lengthOfLength2Users", { length: filteredUsers.length, length2: users.length }) : stText("loading")}
            </span>
            {users && (
              <button
                type="button"
                onClick={refetch}
                className="text-primary-text hover:underline"
              >
                {stText("refresh")}</button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto qb-scroll">
          {/* Loading */}
          {isLoading && !users && (
            <div className="flex items-center justify-center gap-3 py-12">
              <Spinner size="5" />
              <span className="text-sm text-muted-foreground">
                {stText("loadingServerUsers")}</span>
            </div>
          )}

          {/* Error */}
          {error && !users && (
            <div className="px-4 py-6">
              <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:alert-triangle" className="h-5 w-5 text-destructive shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-destructive-text">
                      {stText("failedToLoadUsers")}</p>
                    <p className="text-[12px] text-destructive-text mt-0.5">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {users && sortedUsers.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12">
              <Icon icon="lucide:user-x" className="h-8 w-8 text-muted-foreground/40" aria-hidden />
              <p className="text-sm text-muted-foreground">
                {search ? stText("noUsersMatchYourSearch") : stText("noUsersFoundOnConfiguredServers")}
              </p>
            </div>
          )}

          {/* User list */}
          {sortedUsers.length > 0 && (
            <div>
              {sortedUsers.map((user) => (
                <UserRow
                  key={`${user.serverType}:${user.Id}`}
                  user={user}
                  onSelect={onUserSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </SideFlyout>
  );
};
